import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { InputField } from "@/components/input-field/InputField";
import { SelectComponent } from "@/components/select/Select";
import { AppRoutes } from "@/constants/routes";
import { getTiposSolicitud } from "@/services/request-policies.service";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import { Card, Flex, Grid, GridItem } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  ClasePoliticaEnum,
  requestPolicySchema,
} from "../schema/request-policies.schema";
import {
  handleCreatePolicyHandler,
  resetCreationFormValor,
} from "../schema/handler-functions";
import { PolicyTimeAndDayValueInputView } from "./views/PolicyTimeAndDayInputView";
import { PolicyAmountInputView } from "./views/PolicyAmountInputView";
import { useRoles } from "@/hooks/useRoles";

export const RequestPolicyCreationPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    resolver: zodResolver(requestPolicySchema),
  });

  const navigate = useNavigate();
  const [tiposSolicitud, setTiposSolicitud] = useState<
    { id: string; descripcion: string }[]
  >([]);
  const [loadingTiposSolicitud, setLoadingTiposSolicitud] =
    useState<boolean>(false);
  const [loadingCreatePolicy, setLoadingCreatePolicy] =
    useState<boolean>(false);

  const fetchTiposSolicitud = async () => {
    try {
      setLoadingTiposSolicitud(true);

      const response = await getTiposSolicitud();

      setTiposSolicitud(response);
    } catch {
      // error silenciado — el componente queda con lista vacía
    } finally {
      setLoadingTiposSolicitud(false);
    }
  };

  useEffect(() => {
    if (isActive) fetchTiposSolicitud();
  }, [isActive]);

  const handleCancel = () => {
    navigate(AppRoutes.REQUEST_POLICIES);
  };

  const {fetchPermissions} = useRoles()

  const handleCreatePolicy = () => {
    handleCreatePolicyHandler({
      getValues,
      setLoadingCreatePolicy,
      navigate,
      fetchPermissions,
    });
  };

  const watchedValues = useWatch({ control });

  const hasNoFilledValues = useMemo(() => {
    const { clase, dias, horaDesde, horaHasta, monto, nombre, tipoSolicitud } =
      watchedValues || {};

    return (
      isEmpty(tipoSolicitud) ||
      isEmpty(nombre) ||
      isEmpty(clase) ||
      (isEmpty(monto) && clase?.[0] === ClasePoliticaEnum.TopeDiario) ||
      (isEmpty(dias) && clase?.[0] === ClasePoliticaEnum.RangoSemanal) ||
      (isEmpty(horaDesde) && clase?.[0] === ClasePoliticaEnum.Horario) ||
      (isEmpty(horaHasta) && clase?.[0] === ClasePoliticaEnum.Horario)
    );
  }, [watchedValues]);

  const [clase] = useWatch({
    control,
    name: ["clase"],
  });

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar política de negocio"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreatePolicy}
        addButtonType="submit"
        disableAdd={
          loadingTiposSolicitud || hasNoFilledValues || loadingCreatePolicy
        }
        loading={loadingCreatePolicy}
      />
      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 1fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <SelectComponent
                minHeight="40px"
                labelText="Aplica a"
                control={control}
                name="tipoSolicitud"
                multiple
                placeholder="Buscar..."
                items={tiposSolicitud.map((tipo) => ({
                  label: tipo?.descripcion,
                  value: tipo?.id,
                }))}
                required
                disabled={loadingCreatePolicy || loadingTiposSolicitud}
                loading={loadingTiposSolicitud}
              />
            </GridItem>
            <GridItem paddingX="16px" paddingY="8px">
              <InputField
                control={control}
                inputName="nombre"
                labelText="Nombre"
                placeholder="Ingresar nombre..."
                required
                disabled={loadingCreatePolicy}
              />
            </GridItem>

            <GridItem paddingX="16px" paddingY="8px">
              <SelectComponent
                minHeight="40px"
                items={[
                  {
                    label: ClasePoliticaEnum.TopeDiario,
                    value: ClasePoliticaEnum.TopeDiario,
                  },
                  {
                    label: ClasePoliticaEnum.RangoSemanal,
                    value: ClasePoliticaEnum.RangoSemanal,
                  },
                  {
                    label: ClasePoliticaEnum.Horario,
                    value: ClasePoliticaEnum.Horario,
                  },
                  {
                    label: ClasePoliticaEnum.TopeChequesRespaldo,
                    value: ClasePoliticaEnum.TopeChequesRespaldo,
                  }
                ]}
                control={control}
                name="clase"
                labelText="Clase"
                clearable
                closeOnSelect
                error={errors?.clase}
                required
                disabled={loadingCreatePolicy}
                placeholder="Seleccionar clase"
                handleChange={() => resetCreationFormValor({ setValue })}
              />
            </GridItem>
            <PolicyAmountInputView
              clase={clase}
              control={control}
              errors={errors}
              loading={loadingCreatePolicy}
            />
          </Grid>
          <Flex gap={"50px"}></Flex>
        </Card.Body>
      </Card.Root>

      <PolicyTimeAndDayValueInputView
        control={control}
        clase={clase}
        loading={loadingCreatePolicy}
      />
    </Flex>
  );
};
